import type { LoginResponse } from '@/types/auth';
import { useAuthStore, useClinicStore } from '@/store';
import { useDateFilterStore } from '@/store/dateFilterStore';
import { useQuickActionsStore } from '@/store/quickActionsStore';
import { fetchIPAndLocation } from '@/utils/ipUtils';

/**
 * Processes login response data and sets up all necessary stores and localStorage
 * This is used both for direct login (2FA disabled) and after OTP verification (2FA enabled)
 */
export const processLoginData = async (data: LoginResponse) => {
	if (!data?.tokens?.access_token) {
		return false;
	}

	const { tokens, user, pageConfig, dateFilterConfig } = data;
	const authStore = useAuthStore.getState();
	authStore.setAuth(user || null, tokens, pageConfig || null);

	// Store date filter config if available
	if (dateFilterConfig?.modules) {
		const dateFilterStore = useDateFilterStore.getState();
		dateFilterStore.setDateFilters(dateFilterConfig.modules);
	}

	const currentClinics = user?.userInformation?.currentClinics;
	if (currentClinics && currentClinics.length > 0) {
		const clinicStore = useClinicStore.getState();
		clinicStore.setClinics(currentClinics);
	}

	// Fetch IP and location data for audit logging
	const ipData = await fetchIPAndLocation();

	// Extract clinic IDs and names from user data
	const clinicsDetails = user?.userInformation?.clinicsDetails || [];
	const userClinicIds = clinicsDetails.map((c: any) => c.id).join(',');
	const userClinicNames = clinicsDetails.map((c: any) => c.name).join(',');

	// Store audit user data in localStorage
	const auditUserData = {
		nUserId: user?.userInformation?._id,
		userEmail: user?.email,
		fullName: `${user?.userInformation?.firstName} ${user?.userInformation?.lastName}`,
		port: user?.userInformation?.portsAvailable || 'N/A',
		ipAddress: ipData.ip,
		state: ipData.region || 'N/A',
		country: ipData.country || 'N/A',
		city: ipData.city || 'N/A',
		userType: user?.userInformation?.role || 'N/A',
		userClinicIds,
		userClinicNames,
	};

	localStorage.setItem('auditUser', JSON.stringify(auditUserData));
	
	const currentSalesReps = user?.userInformation?.currentSalesReps;
	if (currentSalesReps && currentSalesReps.length) {
		authStore.updateUserPreferences({
			currentSalesReps: currentSalesReps,
		});
	}

	const currentLocations = user?.userInformation?.currentLocations;
	if (currentLocations && currentLocations.length) {
		authStore.updateUserPreferences({
			currentLocations: currentLocations,
		});
	}

	// Store quick actions from login response
	const quickActions = user?.userInformation?.quickActions;
	if (
		quickActions &&
		typeof quickActions === 'object' &&
		!Array.isArray(quickActions)
	) {
		const quickActionsStore = useQuickActionsStore.getState();
		quickActionsStore.setQuickActions(quickActions);
	}

	return true;
};
